import { useQuery } from '@tanstack/react-query';

import { QUERY_STALE_TIME, isSupabaseConfigured } from '@/config';
import { queryKeys } from '@/lib/data/keys';
import {
  getFixture,
  getH2H365,
  getLiveMatchStats,
  getMatchXg,
  getStandings,
  listFixturesForRange,
  listHeadToHead,
  listLiveFixtures,
  listMatchEvents,
  listPlayerCardsByAfIds,
  listPlayerMatchStats,
} from '@/lib/data/provider';
import { useRealtimeInvalidation } from '@/lib/data/realtime';
import { dayBoundsUtc, dayKey } from '@/lib/i18n/format';

/** [start, end) of the calendar day containing `date`, in app timezone. */
function dayRange(date: Date): { fromIso: string; toIso: string; key: string } {
  const key = dayKey(date);
  const { start, end } = dayBoundsUtc(key);
  return { fromIso: start.toISOString(), toIso: end.toISOString(), key };
}

export function useFixturesForDay(date: Date) {
  const { fromIso, toIso, key } = dayRange(date);
  return useQuery({
    queryKey: queryKeys.fixturesForDay(key),
    queryFn: () => listFixturesForRange(fromIso, toIso),
    staleTime: QUERY_STALE_TIME.fixtures,
    enabled: isSupabaseConfigured,
  });
}

/** Past meetings between the two teams (for the Head-to-Head tab). */
export function useHeadToHead(
  teamA: string | undefined,
  teamB: string | undefined,
  before: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.headToHead(teamA ?? '', teamB ?? ''),
    queryFn: () => listHeadToHead(teamA as string, teamB as string, before as string),
    staleTime: QUERY_STALE_TIME.fixtures,
    enabled: isSupabaseConfigured && Boolean(teamA && teamB && before),
  });
}

export function useLiveFixtures() {
  return useQuery({
    queryKey: queryKeys.liveFixtures,
    queryFn: listLiveFixtures,
    staleTime: QUERY_STALE_TIME.live,
    enabled: isSupabaseConfigured,
  });
}

/** Realtime: any fixtures update refreshes day lists + live strip + group tables. */
export function useFixturesRealtime(enabled = true) {
  useRealtimeInvalidation({
    channel: 'fixtures-all',
    table: 'fixtures',
    invalidate: [['fixtures'], ['standings']],
    enabled: enabled && isSupabaseConfigured,
  });
}

export function useFixture(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.fixture(id ?? 'none'),
    queryFn: () => getFixture(id as string),
    staleTime: QUERY_STALE_TIME.live,
    enabled: isSupabaseConfigured && Boolean(id),
  });
}

export function useMatchEvents(fixtureId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.matchEvents(fixtureId ?? 'none'),
    queryFn: () => listMatchEvents(fixtureId as string),
    staleTime: QUERY_STALE_TIME.live,
    enabled: isSupabaseConfigured && Boolean(fixtureId),
  });
}

/** Realtime for one match: score/status + event timeline. */
export function useMatchRealtime(fixtureId: string | undefined) {
  useRealtimeInvalidation({
    channel: `fixture-${fixtureId ?? 'none'}`,
    table: 'fixtures',
    filter: fixtureId ? `id=eq.${fixtureId}` : undefined,
    // Score/status change → refresh the match, its live ratings, and the table.
    invalidate: fixtureId
      ? [queryKeys.fixture(fixtureId), queryKeys.playerMatchStats(fixtureId), ['standings']]
      : [],
    enabled: isSupabaseConfigured && Boolean(fixtureId),
  });
  useRealtimeInvalidation({
    channel: `events-${fixtureId ?? 'none'}`,
    table: 'match_events',
    filter: fixtureId ? `fixture_id=eq.${fixtureId}` : undefined,
    invalidate: fixtureId
      ? [queryKeys.matchEvents(fixtureId), queryKeys.playerMatchStats(fixtureId)]
      : [],
    enabled: isSupabaseConfigured && Boolean(fixtureId),
  });
}

export function usePlayerMatchStats(fixtureId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.playerMatchStats(fixtureId ?? 'none'),
    queryFn: () => listPlayerMatchStats(fixtureId as string),
    staleTime: QUERY_STALE_TIME.fixtures,
    enabled: isSupabaseConfigured && enabled && Boolean(fixtureId),
  });
}

/** Advanced match stats (xG + shotmap). Only fetched once the match has played,
 *  since the data layer only populates it for live/finished fixtures. */
export function useMatchXg(fixtureId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.matchXg(fixtureId ?? 'none'),
    queryFn: () => getMatchXg(fixtureId as string),
    staleTime: QUERY_STALE_TIME.live,
    enabled: isSupabaseConfigured && enabled && Boolean(fixtureId),
  });
}

/**
 * The 365scores head-to-head block for a fixture, in two steps:
 *   1. read the fixture's 365 GAME id off its xG stats (scores365_game_id),
 *   2. once present, fetch the normalized H2H via the `h2h-365` edge fn.
 * Both `summary` and `meetings` are best-effort (empty when the fixture has no
 * 365 mapping or the provider returns nothing), so the caller self-hides. The
 * H2H of a finished fixture is effectively static, so it's cached as reference.
 */
export function useH2H365(fixtureId: string | undefined) {
  const xgQuery = useMatchXg(fixtureId, Boolean(fixtureId));
  const gameId = xgQuery.data?.stats?.scores365_game_id ?? null;

  const h2hQuery = useQuery({
    queryKey: queryKeys.h2h365(gameId ?? 0),
    queryFn: () => getH2H365(gameId as number),
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured && gameId != null && gameId > 0,
  });

  return {
    data: h2hQuery.data ?? null,
    // Loading while we still need the game id, or while the H2H itself loads.
    isLoading: xgQuery.isLoading || (gameId != null && h2hQuery.isLoading),
  };
}

/**
 * Live in-match stats snapshot, polled every 30s ONLY while the match is live.
 * The interval is off (no polling) once `isLive` is false, so a finished match
 * never wakes the network. Returns null in `data` when there's no fresh row,
 * which the live ticker treats as "hide me".
 */
export function useLiveMatchStats(fixtureId: string | undefined, isLive: boolean) {
  return useQuery({
    queryKey: queryKeys.liveMatchStats(fixtureId ?? 'none'),
    queryFn: () => getLiveMatchStats(fixtureId as string),
    enabled: isSupabaseConfigured && isLive && Boolean(fixtureId),
    // Poll on a 30s cadence while live; React Query stops the timer when the
    // query is disabled (match no longer live) or the screen unmounts.
    refetchInterval: isLive ? 30_000 : false,
    // Just under the poll interval, so the 30s timer (not every screen refocus)
    // drives the refetch cadence — refocusing a live match no longer re-hits the
    // network when the last poll is still fresh.
    staleTime: 25_000,
  });
}

/** Player photo cards for a lineup, keyed by API-Football id. Stable key = the
 *  sorted af-id set, so it caches per match without refetching on every render. */
export function usePlayerCardsByAfIds(afIds: number[], enabled = true) {
  const sorted = [...afIds].sort((a, b) => a - b);
  return useQuery({
    queryKey: queryKeys.playerCards(sorted.join(',')),
    queryFn: () => listPlayerCardsByAfIds(sorted),
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured && enabled && sorted.length > 0,
  });
}

export function useStandings(competitionId: string | undefined, season: number | undefined) {
  return useQuery({
    queryKey: queryKeys.standings(competitionId ?? 'none', season ?? 0),
    queryFn: () => getStandings(competitionId as string, season as number),
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured && Boolean(competitionId) && season !== undefined,
  });
}
